import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import AppShell from "./ui/AppShell";
import BottomNav from "./ui/BottomNav";
import SectionHeader from "./ui/SectionHeader";
import { pricingDisclaimer } from "../data/services/professionalServices";
import { supabase } from "../lib/supabase";

function ProfessionalServicePage({ service }) {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [liveProfiles, setLiveProfiles] = useState([]);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [rentalItems, setRentalItems] = useState([]);
  const [loadingLiveData, setLoadingLiveData] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadLiveData = async () => {
      setLoadingLiveData(true);

      try {
        const [profilesResult, portfoliosResult, rentalResult] = await Promise.all([
          supabase
            .from("professional_profiles")
            .select(
              `
                id,
                role,
                years_experience,
                specialization,
                pricing_range_text,
                service_areas,
                portfolio_summary,
                languages,
                availability_notes,
                verification_status,
                completed_projects,
                average_response_minutes,
                profiles:profile_id (
                  full_name,
                  city,
                  role
                ),
                professional_categories:category_id (
                  slug,
                  title
                )
              `
            ),
          supabase
            .from("professional_portfolios")
            .select(
              `
                id,
                title,
                description,
                media_urls,
                location_text,
                professional_profiles:professional_profile_id (
                  professional_categories:category_id (
                    slug
                  ),
                  profiles:profile_id (
                    full_name
                  )
                )
              `
            ),
          service.slug === "rental-equipment-provider"
            ? supabase
                .from("rental_equipment_inventory")
                .select(
                  `
                    id,
                    equipment_name,
                    category,
                    daily_rental_price,
                    weekly_rental_price,
                    operator_required,
                    condition_status,
                    transport_notes,
                    professional_profiles:professional_profile_id (
                      profiles:profile_id (
                        full_name
                      )
                    )
                  `
                )
            : Promise.resolve({ data: [], error: null }),
        ]);

        if (profilesResult.error) throw profilesResult.error;
        if (portfoliosResult.error) throw portfoliosResult.error;
        if (rentalResult.error) throw rentalResult.error;

        if (!isMounted) return;

        const scopedProfiles = (profilesResult.data || []).filter(
          (item) => item.professional_categories?.slug === service.slug
        );

        const scopedPortfolios = (portfoliosResult.data || []).filter(
          (item) =>
            item.professional_profiles?.professional_categories?.slug === service.slug
        );

        setLiveProfiles(scopedProfiles);
        setPortfolioItems(scopedPortfolios);
        setRentalItems(rentalResult.data || []);
      } catch (error) {
        console.error("Professional service live data error:", error);
        if (isMounted) {
          setLiveProfiles([]);
          setPortfolioItems([]);
          setRentalItems([]);
        }
      } finally {
        if (isMounted) {
          setLoadingLiveData(false);
        }
      }
    };

    loadLiveData();

    return () => {
      isMounted = false;
    };
  }, [service.slug]);

  const trustHighlights = useMemo(() => {
    if (liveProfiles.length === 0) {
      return service.trustIndicators;
    }

    const verifiedCount = liveProfiles.filter(
      (item) => item.verification_status === "verified"
    ).length;
    const totalProjects = liveProfiles.reduce(
      (sum, item) => sum + Number(item.completed_projects || 0),
      0
    );

    return [
      `${liveProfiles.length} live professional profile${
        liveProfiles.length > 1 ? "s" : ""
      } visible`,
      `${verifiedCount} verified profile${verifiedCount === 1 ? "" : "s"} ready`,
      `${totalProjects} completed projects recorded across visible profiles`,
    ];
  }, [liveProfiles, service.trustIndicators]);

  const addBookingModeToCart = (mode) => {
    addToCart({
      type: "service",
      serviceCategory: service.title,
      title: `${service.shortTitle} - ${mode}`,
      description: service.overview,
      basePrice: service.pricing.marketRateMin,
      finalPrice: service.pricing.marketRateMin,
      quantity: 1,
      unit: service.pricing.rateUnit,
      priceType: "inspection",
      duration: "Scope-based",
      warranty: "Scope and deliverables confirmed after review",
      icon: service.icon,
      subCategory: "Professional Service",
      bookingMode: mode,
      pricingModel: service.pricing.pricingModel,
      marketRateMin: service.pricing.marketRateMin,
      marketRateMax: service.pricing.marketRateMax,
      requiresProjectBrief: true,
      scopeNote: pricingDisclaimer,
      workIncludes: service.deliverables,
      notFor: ["Guaranteed approvals", "Silent offline closure"],
    });

    navigate("/checkout");
  };

  return (
    <AppShell maxWidth="1240px">
      <button onClick={() => navigate("/all-services")} style={backBtn}>
        Back to services
      </button>

      <div style={heroCard}>
        <div style={heroGrid}>
          <div>
            <div style={heroMetaRow}>
              <div style={heroBadge}>{service.badge}</div>
              <div style={heroPath}>{service.category}</div>
            </div>
            <h1 style={heroTitle}>{service.title}</h1>
            <p style={heroText}>{service.overview}</p>

            <div style={trustRow}>
              {service.verificationBadges.map((item) => (
                <span key={item} style={pill}>
                  {item}
                </span>
              ))}
            </div>

            <div style={actionRow}>
              <button
                onClick={() => addBookingModeToCart(service.bookingModes[0])}
                style={primaryBtn}
              >
                Book {service.bookingModes[0]}
              </button>
              <button
                onClick={() =>
                  addBookingModeToCart(
                    service.bookingModes.includes("Consultation booking")
                      ? "Consultation booking"
                      : service.bookingModes[0]
                  )
                }
                style={secondaryBtn}
              >
                Book consultation
              </button>
            </div>
          </div>

          <div style={heroSide}>
            <div style={summaryCard}>
              <div style={summaryLabel}>Market pricing</div>
              <div style={summaryValue}>
                Rs {service.pricing.marketRateMin.toLocaleString("en-IN")} - Rs{" "}
                {service.pricing.marketRateMax.toLocaleString("en-IN")}
              </div>
              <div style={summaryText}>
                {service.pricing.pricingModel} - {service.pricing.rateUnit}
              </div>
            </div>

            <div style={summaryCard}>
              <div style={summaryLabel}>Booking modes</div>
              <div style={stackList}>
                {service.bookingModes.map((mode) => (
                  <div key={mode} style={stackItem}>
                    {mode}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={contentGrid}>
        <section style={sectionCard}>
          <SectionHeader
            title="Where This Helps"
            subtitle="Common high-intent situations this category is built for."
          />
          <div style={listGrid}>
            {service.useCases.map((item) => (
              <div key={item} style={miniCard}>
                {item}
              </div>
            ))}
          </div>
        </section>

        <section style={sectionCard}>
          <SectionHeader
            title="Trust Architecture"
            subtitle="Signals that reduce leakage, confusion, and off-platform risk."
          />
          <div style={listGrid}>
            {trustHighlights.map((item) => (
              <div key={item} style={miniCard}>
                {item}
              </div>
            ))}
          </div>
        </section>

        <section style={sectionCard}>
          <SectionHeader
            title="Live Professionals"
            subtitle="Profiles currently available in Supabase for this service category."
          />
          {loadingLiveData ? (
            <div style={miniCard}>Loading live professionals...</div>
          ) : liveProfiles.length === 0 ? (
            <div style={miniCard}>
              No professional profiles are live for this category yet. The category is ready and onboarding can continue from admin.
            </div>
          ) : (
            <div style={listGrid}>
              {liveProfiles.map((profile) => (
                <div key={profile.id} style={profileCard}>
                  <div style={profileHeader}>
                    <div>
                      <div style={profileName}>
                        {profile.profiles?.full_name || "Professional"}
                      </div>
                      <div style={profileMeta}>
                        {profile.professional_categories?.title || service.title}
                      </div>
                    </div>
                    <div
                      style={{
                        ...profileStatus,
                        background:
                          profile.verification_status === "verified"
                            ? "#12311e"
                            : "#2b2410",
                        border:
                          profile.verification_status === "verified"
                            ? "1px solid #28593a"
                            : "1px solid #5b4f1d",
                        color:
                          profile.verification_status === "verified"
                            ? "#b8ffd0"
                            : "#ffd27a",
                      }}
                    >
                      {profile.verification_status === "verified"
                        ? "Verified"
                        : "Pending review"}
                    </div>
                  </div>
                  <div style={profileStats}>
                    <span>{profile.years_experience || 0} yrs exp</span>
                    <span>{profile.completed_projects || 0} projects</span>
                    <span>{profile.profiles?.city || "City pending"}</span>
                  </div>
                  {profile.specialization ? (
                    <div style={profileText}>{profile.specialization}</div>
                  ) : null}
                  {profile.portfolio_summary ? (
                    <div style={profileText}>{profile.portfolio_summary}</div>
                  ) : null}
                  {profile.pricing_range_text ? (
                    <div style={profileSubtle}>{profile.pricing_range_text}</div>
                  ) : null}
                  {Array.isArray(profile.languages) && profile.languages.length > 0 ? (
                    <div style={chipRow}>
                      {profile.languages.map((item) => (
                        <span key={item} style={pill}>
                          {item}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>

        <section style={sectionCard}>
          <SectionHeader
            title="Pricing Structure"
            subtitle={service.pricing.startingPriceLabel}
          />
          <div style={pricingCard}>
            <div style={pricingLine}>
              <span>Pricing model</span>
              <span>{service.pricing.pricingModel}</span>
            </div>
            <div style={pricingLine}>
              <span>Range</span>
              <span>
                Rs {service.pricing.marketRateMin.toLocaleString("en-IN")} - Rs{" "}
                {service.pricing.marketRateMax.toLocaleString("en-IN")}
              </span>
            </div>
            <div style={pricingLine}>
              <span>Rate unit</span>
              <span>{service.pricing.rateUnit}</span>
            </div>
            <p style={pricingNote}>{pricingDisclaimer}</p>
          </div>
        </section>

        <section style={sectionCard}>
          <SectionHeader
            title="Portfolio"
            subtitle="Live portfolio items are shown first, with service-level placeholders as fallback."
          />
          <div style={listGrid}>
            {portfolioItems.length > 0
              ? portfolioItems.map((item) => (
                  <div key={item.id} style={portfolioCard}>
                    <div style={portfolioBadge}>
                      {item.professional_profiles?.profiles?.full_name || "Professional"}
                    </div>
                    <div style={portfolioTitle}>{item.title}</div>
                    <div style={portfolioText}>
                      {item.description || "Portfolio description pending."}
                    </div>
                    {item.location_text ? (
                      <div style={portfolioMeta}>{item.location_text}</div>
                    ) : null}
                  </div>
                ))
              : service.portfolioHighlights.map((item) => (
                  <div key={item} style={portfolioCard}>
                    <div style={portfolioBadge}>Portfolio placeholder</div>
                    <div style={portfolioTitle}>{item}</div>
                    <div style={portfolioText}>
                      Portfolio gallery, photos, drawings, and customer proof can be attached after professional onboarding is verified.
                    </div>
                  </div>
                ))}
          </div>
        </section>

        {service.slug === "rental-equipment-provider" ? (
          <section style={sectionCard}>
            <SectionHeader
              title="Rental Inventory"
              subtitle="Live rental inventory coming from Supabase for equipment-led bookings."
            />
            {loadingLiveData ? (
              <div style={miniCard}>Loading rental inventory...</div>
            ) : rentalItems.length === 0 ? (
              <div style={miniCard}>No rental inventory is live yet for this category.</div>
            ) : (
              <div style={listGrid}>
                {rentalItems.map((item) => (
                  <div key={item.id} style={profileCard}>
                    <div style={profileHeader}>
                      <div>
                        <div style={profileName}>{item.equipment_name}</div>
                        <div style={profileMeta}>{item.category || "Equipment"}</div>
                      </div>
                      <div style={profileStatus}>{item.condition_status || "available"}</div>
                    </div>
                    <div style={profileStats}>
                      <span>
                        Rs {Number(item.daily_rental_price || 0).toLocaleString("en-IN")}/day
                      </span>
                      <span>
                        Rs {Number(item.weekly_rental_price || 0).toLocaleString("en-IN")}/week
                      </span>
                    </div>
                    <div style={profileText}>
                      Provider: {item.professional_profiles?.profiles?.full_name || "Rental provider"}
                    </div>
                    <div style={profileSubtle}>
                      {item.operator_required
                        ? "Operator required"
                        : "Operator optional / not required"}
                    </div>
                    {item.transport_notes ? (
                      <div style={profileText}>{item.transport_notes}</div>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </section>
        ) : null}

        <section style={sectionCard}>
          <SectionHeader
            title="Testimonials"
            subtitle="Reserved for customer proof after verified completions."
          />
          <div style={listGrid}>
            {["Scope clarity", "Trustworthy coordination", "Responsive execution"].map(
              (item) => (
                <div key={item} style={testimonialCard}>
                  <div style={testimonialTitle}>{item}</div>
                  <div style={testimonialText}>
                    Placeholder for verified completion reviews and future rating-backed proof.
                  </div>
                </div>
              )
            )}
          </div>
        </section>

        <section style={sectionCard}>
          <SectionHeader
            title="Service Areas"
            subtitle="Operational areas can later be filtered by city launch control and professional availability."
          />
          <div style={trustRow}>
            {service.serviceAreas.map((area) => (
              <span key={area} style={pill}>
                {area}
              </span>
            ))}
          </div>
        </section>

        <section style={sectionCard}>
          <SectionHeader title="FAQ" subtitle="Customer trust and scope clarity answers." />
          <div style={faqWrap}>
            {service.faqs.map((faq) => (
              <div key={faq.question} style={faqCard}>
                <div style={faqQuestion}>{faq.question}</div>
                <div style={faqAnswer}>{faq.answer}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <BottomNav />
    </AppShell>
  );
}

const backBtn = {
  marginBottom: "20px",
  padding: "10px 16px",
  border: "1px solid #444",
  background: "#111",
  color: "#fff",
  borderRadius: "10px",
  cursor: "pointer",
};

const heroCard = {
  background: "linear-gradient(135deg, #121212, #1b1b1b)",
  border: "1px solid #262626",
  borderRadius: "24px",
  padding: "28px",
  boxShadow: "0 22px 50px rgba(0,0,0,0.28)",
};

const heroGrid = {
  display: "grid",
  gridTemplateColumns: "1.35fr 0.95fr",
  gap: "22px",
};

const heroMetaRow = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
  marginBottom: "16px",
};

const heroBadge = {
  padding: "8px 12px",
  borderRadius: "999px",
  border: "1px solid #4b3d1b",
  background: "#1f1608",
  color: "#f5d188",
  fontSize: "12px",
  fontWeight: "700",
};

const heroPath = {
  padding: "8px 12px",
  borderRadius: "999px",
  border: "1px solid #2a2a2a",
  background: "#111",
  color: "#d1d1d1",
  fontSize: "12px",
};

const heroTitle = {
  margin: 0,
  fontSize: "40px",
  lineHeight: 1.08,
};

const heroText = {
  color: "#c8c8c8",
  lineHeight: "1.8",
  marginTop: "14px",
};

const trustRow = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
};

const pill = {
  padding: "8px 12px",
  borderRadius: "999px",
  border: "1px solid #2b2b2b",
  background: "#0d0d0d",
  color: "#ebebeb",
  fontSize: "12px",
};

const actionRow = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
  marginTop: "18px",
};

const primaryBtn = {
  padding: "12px 18px",
  border: "none",
  background: "#f2f2f2",
  color: "#111",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: "700",
};

const secondaryBtn = {
  ...primaryBtn,
  background: "#111",
  color: "#fff",
  border: "1px solid #343434",
};

const heroSide = {
  display: "grid",
  gap: "14px",
};

const summaryCard = {
  background: "#0e0e0e",
  border: "1px solid #252525",
  borderRadius: "18px",
  padding: "18px",
};

const summaryLabel = {
  color: "#9a9a9a",
  fontSize: "13px",
  marginBottom: "8px",
};

const summaryValue = {
  fontSize: "24px",
  fontWeight: "700",
};

const summaryText = {
  color: "#c9c9c9",
  marginTop: "8px",
  lineHeight: "1.7",
};

const stackList = {
  display: "grid",
  gap: "10px",
};

const stackItem = {
  background: "#151515",
  border: "1px solid #262626",
  borderRadius: "12px",
  padding: "10px 12px",
  color: "#efefef",
};

const contentGrid = {
  display: "grid",
  gap: "18px",
  marginTop: "24px",
};

const sectionCard = {
  background: "linear-gradient(180deg, #121212, #0b0b0b)",
  border: "1px solid #232323",
  borderRadius: "22px",
  padding: "22px",
};

const listGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "14px",
};

const miniCard = {
  background: "#101010",
  border: "1px solid #222",
  borderRadius: "16px",
  padding: "16px",
  lineHeight: "1.7",
  color: "#ddd",
};

const pricingCard = {
  background: "#101010",
  border: "1px solid #252525",
  borderRadius: "18px",
  padding: "18px",
};

const pricingLine = {
  display: "flex",
  justifyContent: "space-between",
  gap: "14px",
  padding: "10px 0",
  borderBottom: "1px solid #1f1f1f",
};

const pricingNote = {
  color: "#c5c5c5",
  lineHeight: "1.7",
  marginTop: "14px",
  marginBottom: 0,
};

const portfolioCard = {
  background: "#0f1011",
  border: "1px solid #232629",
  borderRadius: "16px",
  padding: "16px",
};

const portfolioBadge = {
  color: "#88d8ff",
  fontSize: "12px",
  fontWeight: "700",
  marginBottom: "8px",
};

const portfolioTitle = {
  fontWeight: "700",
  marginBottom: "8px",
};

const portfolioText = {
  color: "#c6c6c6",
  lineHeight: "1.7",
};

const portfolioMeta = {
  marginTop: "10px",
  color: "#9f9f9f",
  fontSize: "13px",
};

const testimonialCard = {
  background: "#12100d",
  border: "1px solid #30281d",
  borderRadius: "16px",
  padding: "16px",
};

const testimonialTitle = {
  fontWeight: "700",
  marginBottom: "8px",
};

const testimonialText = {
  color: "#d1cbc2",
  lineHeight: "1.7",
};

const profileCard = {
  background: "#0f1011",
  border: "1px solid #232629",
  borderRadius: "16px",
  padding: "16px",
  display: "grid",
  gap: "10px",
};

const profileHeader = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  flexWrap: "wrap",
  alignItems: "flex-start",
};

const profileName = {
  fontWeight: "700",
  fontSize: "18px",
};

const profileMeta = {
  color: "#a8a8a8",
  marginTop: "6px",
  fontSize: "13px",
};

const profileStatus = {
  padding: "7px 10px",
  borderRadius: "999px",
  border: "1px solid #333",
  background: "#171717",
  color: "#f1f1f1",
  fontSize: "12px",
  fontWeight: "700",
  textTransform: "capitalize",
};

const profileStats = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  color: "#e0e0e0",
  fontSize: "13px",
};

const profileText = {
  color: "#cfcfcf",
  lineHeight: "1.7",
};

const profileSubtle = {
  color: "#9f9f9f",
  fontSize: "13px",
  lineHeight: "1.6",
};

const chipRow = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
};

const faqWrap = {
  display: "grid",
  gap: "12px",
};

const faqCard = {
  background: "#101010",
  border: "1px solid #242424",
  borderRadius: "16px",
  padding: "16px",
};

const faqQuestion = {
  fontWeight: "700",
  marginBottom: "8px",
};

const faqAnswer = {
  color: "#c9c9c9",
  lineHeight: "1.7",
};

export default ProfessionalServicePage;
